/**
 * DEPLETIONS-INVENTORY MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// InventoryChart.jsx — a snapshot bar chart of current on-hand inventory, one
// bar per Group-By bucket (e.g. grouped by Flavor + Size → a bar per flavor/
// size). Inventory is a point-in-time snapshot (no month-over-month history),
// so a bar chart is the only shape that makes sense — no line/timeline.
import React, { useRef, useState, useLayoutEffect } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList, Label,
} from "recharts";
import "./di-chart.css";

const COLORS = [
  "#f5a623", "#3b82f6", "#4eca8d", "#e8526a", "#a78bfa",
  "#fb923c", "#22d3ee", "#84cc16", "#f472b6", "#facc15",
  "#60a5fa", "#34d399", "#f87171", "#c084fc", "#fbbf24",
];
const AXIS_TEXT = "var(--muted)";  // theme chrome — follows light/dark
const GRID_LINE = "rgba(140,160,190,0.18)";
const AXIS_LINE = "rgba(140,160,190,0.28)";

const kfmt = (v) => (v >= 1000 ? `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k` : Math.round(v).toLocaleString());

// Split a long label into at most 2 lines that each fit `maxChars`. Breaks on
// word boundaries when possible; truncates the 2nd line with an ellipsis if even
// two lines can't hold it.
function splitTwoLines(text, maxChars) {
  if (text.length <= maxChars) return [text];
  const words = text.split(" ");
  if (words.length === 1) {
    const head = text.slice(0, maxChars);
    let tail = text.slice(maxChars);
    if (tail.length > maxChars) tail = tail.slice(0, Math.max(1, maxChars - 1)) + "…";
    return [head, tail];
  }
  let l1 = "", i = 0;
  for (; i < words.length; i++) {
    const next = l1 ? `${l1} ${words[i]}` : words[i];
    if (next.length > maxChars && l1) break;
    l1 = next;
  }
  let l2 = words.slice(i).join(" ");
  if (l2.length > maxChars) l2 = l2.slice(0, Math.max(1, maxChars - 1)) + "…";
  return [l1, l2];
}

// Custom X-axis tick that wraps long labels onto a 2nd line. Horizontal layout
// for a readable number of bars; angled (and still wrappable) once they crowd.
function WrappedTick({ x, y, payload, many, maxChars }) {
  const lines = splitTwoLines(String(payload?.value ?? ""), maxChars);
  if (many) {
    return (
      <g transform={`translate(${x},${y})`}>
        <text transform="rotate(-32)" textAnchor="end" fill={AXIS_TEXT} fontSize={11}>
          {lines.map((ln, i) => (
            <tspan key={i} x={0} dy={i === 0 ? 10 : 12}>{ln}</tspan>
          ))}
        </text>
      </g>
    );
  }
  return (
    <text x={x} y={y} textAnchor="middle" fill={AXIS_TEXT} fontSize={11}>
      {lines.map((ln, i) => (
        <tspan key={i} x={x} dy={i === 0 ? 13 : 12}>{ln}</tspan>
      ))}
    </text>
  );
}

export default function InventoryChart({ rows = [], dimCols = [] }) {
  const areaRef = useRef(null);
  const [areaW, setAreaW] = useState(0);
  useLayoutEffect(() => {
    const el = areaRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => setAreaW(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const data = rows
    .map((r) => ({
      label: dimCols.map((c) => r[c.id]).filter((v) => v && v !== "—").join(" ") || "—",
      inventory: Number(r.current_inventory) || 0,
    }))
    .filter((d) => d.inventory > 0)
    .sort((a, b) => b.inventory - a.inventory);

  if (!data.length) {
    return (
      <div className="chart-prompt">
        <div className="chart-prompt-icon">📦</div>
        <div className="chart-prompt-text">No inventory to chart for the current filters.</div>
      </div>
    );
  }

  const many = data.length > 8;

  // Decide wrapping from the measured plot width. Reserve the Y-axis (64) + L/R
  // margins (8 + 16). If a label exceeds what one band can hold, it wraps to a
  // 2nd line; the axis grows to make room.
  const effW = areaW || 900;
  const band = Math.max(20, (effW - 88) / data.length);
  const maxChars = many ? 16 : Math.max(5, Math.min(24, Math.floor(band / 6.4)));
  const anyWrapped = data.some((d) => d.label.length > maxChars);
  const xHeight = many ? 100 : anyWrapped ? 48 : 28;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="chart-tooltip">
        <div className="chart-tooltip-label">{label}</div>
        <div className="chart-tooltip-total">Inventory: <strong>{Math.round(payload[0].value).toLocaleString()}</strong> units</div>
      </div>
    );
  };

  const BarLabel = ({ x, y, width, value }) => {
    if (!value) return null;
    return (
      <text x={x + width / 2} y={y - 5} textAnchor="middle" fill={AXIS_TEXT} fontSize={11} fontWeight={600}>
        {kfmt(value)}
      </text>
    );
  };

  return (
    <div className="chart-wrap">
      <div className="chart-controls">
        <span className="chart-no-data" style={{ fontStyle: "normal", color: AXIS_TEXT }}>
          Current inventory by {dimCols.map((c) => c.label).join(" + ") || "row"}
        </span>
      </div>
      <div className="chart-area" ref={areaRef}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 26, right: 16, left: 8, bottom: many ? 8 : 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_LINE} vertical={false} />
            <XAxis
              dataKey="label"
              tick={<WrappedTick many={many} maxChars={maxChars} />}
              axisLine={{ stroke: AXIS_LINE }}
              tickLine={false}
              interval={0}
              height={xHeight}
            />
            <YAxis tickFormatter={kfmt} tick={{ fill: AXIS_TEXT, fontSize: 11 }} axisLine={false} tickLine={false} width={64}>
              <Label value="Units" angle={-90} position="insideLeft" offset={12}
                style={{ fill: AXIS_TEXT, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }} />
            </YAxis>
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(232,93,38,0.10)" }} />
            <Bar dataKey="inventory" radius={[3, 3, 0, 0]} maxBarSize={64}>
              {data.map((d, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              <LabelList dataKey="inventory" content={<BarLabel />} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
